import { useEffect, useState } from "react";
import { Drawer, Tabs, Collapse, Spin, Empty, Descriptions, Alert, Button, Tag, message } from "antd";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
    GetAuditoriaClinicaDetalle,
    PostIniciarAuditoria,
    PostMarcarAuditada,
} from "../../Service/Service.auditoriaClinica";
import AuditoriaHistorialCorrecciones from "./AuditoriaHistorialCorrecciones";
import AuditCorrectionModal from "./AuditCorrectionModal";
import { ESTADO_AUDITORIA_TAG } from "./AuditoriaClinicaTabla";
import usePermissions from "../../hook/usePermissions";

const { Panel } = Collapse;

const CITA_ESTADO_FINALIZADA = 6;

// AUDITORIA-CLINICA-FASE-1 -- drawer 100% lectura por defecto.
//
// AUDITORIA-CLINICA-FASE-2: agrega el UNICO punto de escritura visible en el
// drawer, el boton "Corregir atención" -- visible SOLO si el actor tiene
// `auditoria.clinica.correct` Y la cita esta Finalizada (id_estado_cita = 6);
// si falta cualquiera de las dos condiciones el boton no se renderiza (mismo
// patron ya usado en el repo para acciones condicionadas por permiso, ver
// PlanListado.js `hasPermission(...) && (...)`), nunca disabled+tooltip.
// Su ubicacion es la barra de acciones del CUERPO del Drawer -- ver FIX-43
// junto al JSX del boton.
// AUDITORIA-CLINICA-FASE-4: el detalle de auditoria es la UNICA lectura que
// muestra todos los estados de una receta (el flujo normal solo ve la
// vigente). Se distingue vigente / reemplazada / anulada y, cuando existe, se
// indica la receta que la sustituyo -- la relacion sale de la FK
// `reemplaza_a` de la version nueva, nunca de fechas ni de la posicion.
const RECETA_ESTADO_TAG = {
    Activa: { color: "green", label: "Vigente" },
    Reemplazada: { color: "orange", label: "Reemplazada" },
    Anulada: { color: "red", label: "Anulada" },
};

export const RecetaFila = ({ receta, recetas }) => {
    const estado = receta.estado || "Activa";
    const tag = RECETA_ESTADO_TAG[estado] || { color: "default", label: estado };
    const sustituta = (recetas || []).find((r) => r.reemplaza_a === receta.idreceta);
    return (
        <div data-testid={`receta-${receta.idreceta}`}>
            <Tag color={tag.color}>{tag.label}</Tag>
            Receta #{receta.idreceta}: {receta.medicamentos} — {receta.indicaciones}
            {sustituta && <div className="text-muted ps-2">→ sustituida por la receta #{sustituta.idreceta}</div>}
        </div>
    );
};

const Lista = ({ items, render }) =>
    !items || items.length === 0 ? (
        <Empty description="Sin registros" />
    ) : (
        <div className="d-flex flex-column gap-2">{items.map(render)}</div>
    );

function AuditoriaClinicaDetalleDrawer({ open, idcita, onClose, onCorrectionSaved }) {
    const [loading, setLoading] = useState(true);
    const [detalle, setDetalle] = useState(null);
    const [error, setError] = useState(null);
    const [reloadToken, setReloadToken] = useState(0);
    const [historialKey, setHistorialKey] = useState(0);
    const [correctionModalOpen, setCorrectionModalOpen] = useState(false);
    const [accionWorkflow, setAccionWorkflow] = useState(false);
    const { hasPermission } = usePermissions();

    useEffect(() => {
        if (!open || !idcita) return;
        let cancelado = false;
        (async () => {
            setLoading(true);
            setError(null);
            const res = await GetAuditoriaClinicaDetalle(idcita);
            if (cancelado) return;
            if (res?.success) {
                setDetalle(res.data);
            } else {
                setDetalle(null);
                setError(res?.message || "No se pudo obtener el detalle de la atención.");
            }
            setLoading(false);
        })();
        return () => { cancelado = true; };
    }, [open, idcita, reloadToken]);

    const handleCorrectionSaved = () => {
        // Refresca el detalle (para reflejar `after`) y el historial (para la
        // nueva corrección), sin recargar la pagina completa; tambien notifica
        // al listado (cantidad_correcciones/ultima_correccion de la fila).
        setReloadToken((t) => t + 1);
        setHistorialKey((k) => k + 1);
        onCorrectionSaved && onCorrectionSaved(idcita);
    };

    const s = detalle?.secciones || {};
    const citaFinalizada = Number(detalle?.estado_cita?.idestado_cita) === CITA_ESTADO_FINALIZADA;
    const tienePermisoCorregir = hasPermission("auditoria.clinica.correct");

    // AUDITORIA-CLINICA-WORKFLOW-44 §7: el estado de REVISION decide QUE
    // acciones se ofrecen. AUDITADA es cierre: no se ofrece corregir ni
    // re-cerrar (el backend tambien lo rechaza con 409, esto solo evita
    // ofrecer una accion que iba a fallar).
    const estadoAuditoria = detalle?.estado_auditoria?.estado || "PENDIENTE";
    const yaAuditada = estadoAuditoria === "AUDITADA";
    const puedeAccionarWorkflow = tienePermisoCorregir && citaFinalizada && !yaAuditada;
    const puedeCorregir = tienePermisoCorregir && citaFinalizada && !yaAuditada;

    const ejecutarAccionWorkflow = async (fn, exito) => {
        setAccionWorkflow(true);
        const res = await fn();
        setAccionWorkflow(false);
        if (res?.success) {
            message.success(exito);
            setReloadToken((t) => t + 1);
            onCorrectionSaved && onCorrectionSaved(idcita);
        } else {
            message.error(res?.message || "No se pudo actualizar el estado de la auditoría.");
            // Relee igual: si fallo por 409 el estado real ya cambio y la
            // pantalla debe reflejarlo en vez de quedar desincronizada.
            setReloadToken((t) => t + 1);
        }
    };

    const items = detalle
        ? [
              {
                  key: "resumen",
                  label: "Resumen",
                  children: (
                      <Descriptions size="small" column={1} bordered>
                          <Descriptions.Item label="Paciente">{detalle.paciente?.nombres_completos || "SIN DETERMINAR"}</Descriptions.Item>
                          <Descriptions.Item label="Documento">{detalle.paciente?.cedula || "—"}</Descriptions.Item>
                          <Descriptions.Item label="Doctor">{detalle.doctor?.nombre || "SIN DETERMINAR"}</Descriptions.Item>
                          <Descriptions.Item label="Clínica">{detalle.clinica?.nombre || "—"}</Descriptions.Item>
                          <Descriptions.Item label="Estado de la cita">{detalle.estado_cita?.estado || "—"}</Descriptions.Item>
                          <Descriptions.Item label="Fecha">
                              {detalle.fecha ? format(new Date(detalle.fecha), "dd/MM/yyyy HH:mm", { locale: es }) : "—"}
                          </Descriptions.Item>
                      </Descriptions>
                  ),
              },
              {
                  key: "clinico",
                  label: "Secciones clínicas",
                  children: (
                      <Collapse defaultActiveKey={[]}>
                          <Panel header="Motivo de consulta" key="motivo">
                              <p>{s.motivo || "Sin motivo registrado"}</p>
                          </Panel>
                          <Panel header={`Signos vitales (${s.signos_vitales?.length || 0})`} key="signos">
                              <Lista items={s.signos_vitales} render={(r) => <div key={r.idsignosvitales}>Presión: {r.presion || "—"} · Pulso: {r.pulso ?? "—"} · Temp: {r.temperatura ?? "—"}</div>} />
                          </Panel>
                          <Panel header={`Enfermedad actual (${s.enfermedad_actual?.length || 0})`} key="enfermedad">
                              <Lista items={s.enfermedad_actual} render={(r) => <p key={r.idenfermedad_actual_cita}>{r.enfermedad_actual}</p>} />
                          </Panel>
                          <Panel header={`Diagnósticos (${s.diagnosticos?.length || 0})`} key="diagnosticos">
                              <Lista items={s.diagnosticos} render={(r) => <div key={r.iddiagnostico_cita_paciente}>{r.diagnostico?.codigo} - {r.diagnostico?.nombre} ({r.typo_diagnostico || "—"})</div>} />
                          </Panel>
                          <Panel header={`Plan de tratamiento (${s.plan_tratamiento?.length || 0})`} key="plan_tratamiento">
                              <Lista items={s.plan_tratamiento} render={(r) => <p key={r.idplan_tratamiento}>{r.plan_tratamiento}</p>} />
                          </Panel>
                          <Panel header={`Notas de evolución (${s.nota_evolucion?.length || 0})`} key="evolucion">
                              <Lista items={s.nota_evolucion} render={(r) => <p key={r.idnota_evolucion}>{r.nota_evolucion}</p>} />
                          </Panel>
                          <Panel header={`Recetas (${s.recetas?.length || 0})`} key="recetas">
                              <Lista items={s.recetas} render={(r) => <RecetaFila key={r.idreceta} receta={r} recetas={s.recetas} />} />
                          </Panel>
                          <Panel header={`Órdenes de laboratorio (${s.examenes_laboratorio?.length || 0})`} key="lab">
                              <Lista
                                  items={s.examenes_laboratorio}
                                  render={(r) => (
                                      <div key={r.idexamen_cita}>
                                          Orden {r.numero_orden || r.idexamen_cita} — {r.estado || "—"}
                                          {Array.isArray(r.examen_cita_paciente_solicitud) && r.examen_cita_paciente_solicitud.length > 0 && (
                                              <ul className="mb-0">
                                                  {r.examen_cita_paciente_solicitud.map((l) => (
                                                      <li key={l.idexamen_cita_paciente_solicitud}>{l.ExamenTipo?.descripcion}</li>
                                                  ))}
                                              </ul>
                                          )}
                                      </div>
                                  )}
                              />
                          </Panel>
                          <Panel header={`Órdenes de imagen (${s.examenes_imagen?.length || 0})`} key="imagen">
                              <Lista items={s.examenes_imagen} render={(r) => <div key={r.idexamen_imagen}>Orden {r.numero_orden || r.idexamen_imagen} — {r.estado || "—"}</div>} />
                          </Panel>
                          <Panel header={`Órdenes de imagen externas (${s.examenes_imagen_externa?.length || 0})`} key="imagen_externa">
                              <Lista items={s.examenes_imagen_externa} render={(r) => <div key={r.idexamen_imagen_externa}>{r.examen_externo} — {r.estado || "—"}</div>} />
                          </Panel>
                          <Panel header={`Certificados (${(s.certificados?.ordenes?.length || 0) + (s.certificados?.documentos?.length || 0)})`} key="certificados">
                              <Lista items={s.certificados?.ordenes} render={(r) => <div key={r.idcertificado_orden}>Orden certificado #{r.idcertificado_orden} — {r.numero_certificado || "—"}</div>} />
                              <Lista items={s.certificados?.documentos} render={(r) => <div key={r.idcertificado}>Documento certificado #{r.idcertificado} — {r.codigo || "—"}</div>} />
                          </Panel>
                          <Panel header={`Resultados (${s.resultados?.data?.length || 0})`} key="resultados">
                              {s.resultados?.fuente && <Alert type="info" showIcon message={s.resultados.fuente} className="mb-2" />}
                              <Lista items={s.resultados?.data} render={(r) => <div key={r.id}>Resultado #{r.id} — {r.estado || "—"}</div>} />
                          </Panel>
                      </Collapse>
                  ),
              },
              {
                  key: "historial",
                  label: "Historial de correcciones",
                  children: <AuditoriaHistorialCorrecciones idcita={idcita} key={historialKey} />,
              },
          ]
        : [];

    return (
        <Drawer
            title={idcita ? `Auditoría de la atención #${idcita}` : "Auditoría de la atención"}
            width={720}
            open={open}
            onClose={onClose}
            destroyOnClose
        >
            {loading && <Spin />}
            {!loading && error && <Alert type="error" showIcon message={error} />}
            {/* AUDITORIA-CLINICA-BOTON-CORREGIR-FIX-43: el boton vive en el
                CUERPO del Drawer (barra de acciones sobre las tabs), no en
                `Drawer.extra`. Estaba en el header y el header no se ve en el
                navegador real -- con `puedeCorregir=true` el boton llegaba al
                DOM (dentro de `.ant-drawer-extra`) pero quedaba en una zona no
                visible. El cuerpo del Drawer es la region verificada visible
                (es donde se ven las tabs). Sigue siendo UN solo boton, con la
                MISMA condicion permiso + cita finalizada. */}
            {!loading && !error && detalle && (
                <div className="d-flex justify-content-between align-items-center mb-3" data-testid="audit-acciones">
                    <div>
                        <span className="text-muted me-2">Estado de auditoría:</span>
                        <Tag color={ESTADO_AUDITORIA_TAG[estadoAuditoria]?.color} data-testid="drawer-estado-auditoria">
                            {ESTADO_AUDITORIA_TAG[estadoAuditoria]?.label || estadoAuditoria}
                        </Tag>
                        {yaAuditada && (
                            <span className="text-muted small" data-testid="drawer-auditada-info">
                                {detalle.estado_auditoria?.completed_at
                                    ? format(new Date(detalle.estado_auditoria.completed_at), "dd/MM/yyyy HH:mm", { locale: es })
                                    : "—"}
                                {detalle.estado_auditoria?.completed_by?.full_name
                                    ? ` · ${detalle.estado_auditoria.completed_by.full_name}`
                                    : ""}
                            </span>
                        )}
                    </div>
                    <div className="d-flex gap-2">
                        {puedeAccionarWorkflow && estadoAuditoria === "PENDIENTE" && (
                            <Button
                                loading={accionWorkflow}
                                onClick={() => ejecutarAccionWorkflow(() => PostIniciarAuditoria(idcita), "Revisión de auditoría iniciada")}
                            >
                                Iniciar auditoría
                            </Button>
                        )}
                        {puedeCorregir && (
                            <Button type="primary" onClick={() => setCorrectionModalOpen(true)}>
                                Corregir atención
                            </Button>
                        )}
                        {puedeAccionarWorkflow && (
                            <Button
                                loading={accionWorkflow}
                                onClick={() =>
                                    ejecutarAccionWorkflow(
                                        () => PostMarcarAuditada(idcita, estadoAuditoria),
                                        "Atención marcada como auditada"
                                    )
                                }
                            >
                                Marcar como auditada
                            </Button>
                        )}
                    </div>
                </div>
            )}
            {!loading && !error && detalle && <Tabs items={items} />}

            {puedeCorregir && (
                <AuditCorrectionModal
                    open={correctionModalOpen}
                    idcita={idcita}
                    detalle={detalle}
                    onClose={() => setCorrectionModalOpen(false)}
                    onSaved={handleCorrectionSaved}
                />
            )}
        </Drawer>
    );
}

export default AuditoriaClinicaDetalleDrawer;
